/**
 * @module CopyProjectModal
 * @description A modal component for copying a project to a new owner with selected parts.
 */

import React, { useEffect, useState } from "react"
import { Modal, Input, Checkbox } from "antd"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/free-solid-svg-icons"
import { CheckboxChangeEvent } from "antd/es/checkbox"

import { useCopyProjectMutation } from "features/Projects/api/useProjectsMutations"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { useProjectStore } from "stores/projectStore"

// Types for copy options
interface CopyOptions {
  metadata: boolean
  qnr: boolean
  data: boolean
  docs: boolean
  queries: boolean
}

type CopyOptionKey = keyof CopyOptions

interface CopyOptionLabels {
  metadata: string
  qnr: string
  data: string
  docs: string
  queries: string
}

interface CopyProjectModalProps {
  userData: string[]
}

/**
 * @function CopyProjectModal
 * @description A modal component for copying a project to a new owner with selected parts.
 * @param props - The component props
 * @param props.userData - Array of available users in the unit
 * @returns The CopyProjectModal component
 */
const CopyProjectModal: React.FC<CopyProjectModalProps> = ({ userData }) => {
  const project = useProjectStore((state) => state.project)
  const copyProjectMutation = useCopyProjectMutation()
  const navigate = useNavigate()

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [displayUserData, setDisplayUserData] = useState<string[]>([])
  const [newProjectName, setNewProjectName] = useState<string>("")
  const [selectedOwner, setSelectedOwner] = useState<string>("")
  const [copyOptions, setCopyOptions] = useState<CopyOptions>({
    metadata: true,
    qnr: true,
    data: true,
    docs: true,
    queries: true,
  })

  const copyOptionLabels: CopyOptionLabels = {
    metadata: "Project Info",
    qnr: "Questionnaire",
    data: "Data",
    docs: "Documents",
    queries: "Queries",
  }

  // Prepare user data with project creator first
  useEffect(() => {
    if (userData.length > 0) {
      const userArray = [
        project.Created_by,
        ...userData.filter(
          (user: string) =>
            user.toLowerCase() !== project.Created_by.toLowerCase()
        ),
      ]
      setDisplayUserData(userArray)
    }
  }, [userData, project.Created_by])

  /**
   * @async
   * @function copyProject
   * @description Copies the project with selected options to a new owner.
   * @returns {Promise<void>}
   */
  const copyProject = async (): Promise<void> => {
    if (!newProjectName.trim() || !selectedOwner) {
      notify.error(
        "Error Copying Project!",
        "Please enter a project name and select a new owner."
      )
      return
    }

    const copyParts: string = Object.entries(copyOptions)
      .filter(([, value]: [string, boolean]) => value)
      .map(([key]: [string, boolean]) => key)
      .join(",")

    try {
      await copyProjectMutation.mutateAsync({
        id: project.id,
        owner: project.Created_by,
        copy: copyParts,
        name: newProjectName.trim(),
        newOwner: selectedOwner,
      })
      cancel() // Reset form and close modal
      navigate("/home")
    } catch (error) {
      // Notifications are handled by the mutation; just log.
      console.error("Error copying project:", error)
    }
  }

  /**
   * @function showModal
   * @description Sets the modal visibility to true.
   */
  const showModal = (): void => {
    setIsModalVisible(true)
  }

  /**
   * @function cancel
   * @description Resets the modal state and closes it.
   */
  const cancel = (): void => {
    setIsModalVisible(false)
    setNewProjectName("")
    setSelectedOwner("")
    setCopyOptions({
      metadata: true,
      qnr: true,
      data: true,
      docs: true,
      queries: true,
    })
  }

  /**
   * @function handleCopyOptionChange
   * @description Handles changes in the copy options checkboxes.
   * @param {CheckboxChangeEvent} e - The checkbox change event
   */
  const handleCopyOptionChange = (e: CheckboxChangeEvent): void => {
    const { name, checked } = e.target
    if (name && name in copyOptions) {
      setCopyOptions({
        ...copyOptions,
        [name as CopyOptionKey]: checked,
      })
    }
  }

  /**
   * @function handleOwnerChange
   * @description Handles changes in the owner selection.
   * @param {CheckboxChangeEvent} e - The checkbox change event
   */
  const handleOwnerChange = (e: CheckboxChangeEvent): void => {
    const { value, checked } = e.target
    setSelectedOwner(checked ? (value as string) : "")
  }

  /**
   * @function handleProjectNameChange
   * @description Handles changes in the project name input.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   */
  const handleProjectNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNewProjectName(e.target.value)
  }

  /**
   * @function isFormValid
   * @description Checks if the form is valid for submission.
   * @returns {boolean} True if form is valid, false otherwise
   */
  const isFormValid = (): boolean => {
    return newProjectName.trim().length > 0 && selectedOwner.length > 0
  }

  return (
    <>
      <DataQButton onClick={showModal} type="primary" className="text-black">
        Copy Project
        <FontAwesomeIcon className="ml-2" icon={faCopy} />
      </DataQButton>

      <Modal
        open={isModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
        width={600}
      >
        <h2 className="text-xl font-semibold m-0">Copy Project</h2>

        <Input
          placeholder="Enter new project name"
          value={newProjectName}
          onChange={handleProjectNameChange}
          className="my-4"
          maxLength={100}
        />

        <h3 className="text-lg font-medium m-0 mt-2">Select New Owner</h3>
        <div className="max-h-40 overflow-y-auto">
          {displayUserData.map((user: string) => (
            <div key={user} className="flex items-center mb-2">
              <Checkbox
                value={user}
                checked={selectedOwner === user}
                onChange={handleOwnerChange}
                className="mr-2"
              />
              <span>{user}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        <h3 className="text-lg font-medium mt-0 mb-2">Select Parts to Copy</h3>
        <div className="flex flex-wrap">
          {(Object.entries(copyOptions) as [CopyOptionKey, boolean][]).map(
            ([key, value]) => (
              <div key={key} className="w-1/2 mb-2 flex items-center">
                <Checkbox
                  name={key}
                  checked={value}
                  onChange={handleCopyOptionChange}
                  className="mr-2"
                />
                <span>{copyOptionLabels[key]}</span>
              </div>
            )
          )}
        </div>

        <div className="flex flex-row mt-5 justify-between">
          <DataQButton type="secondary" onClick={cancel}>
            Cancel
          </DataQButton>
          <DataQButton
            type="primary"
            onClick={copyProject}
            isLoading={copyProjectMutation.isPending}
            isDisabled={!isFormValid() || copyProjectMutation.isPending}
          >
            Copy Project
          </DataQButton>
        </div>
      </Modal>
    </>
  )
}

export default CopyProjectModal
